import React from 'react'
import { useSwipeTabs } from '@/hooks/useSwipeTabs'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

interface SwipeableTabContentProps {
    /** Ordered list of tabs — must match the order of `children`. */
    tabs: { id: string; label: string }[]
    /** Currently active tab id. */
    activeId: string
    /** Called with the new tab id when a swipe gesture commits. */
    onChange: (id: string) => void
    /**
     * One React node per tab, in the same order as `tabs`.
     * Only the active panel and its immediate neighbours are mounted;
     * the rest are replaced with `null` to avoid unnecessary renders.
     */
    children: React.ReactNode[]
    /** Extra class names for the outer (overflow-hidden) container. */
    className?: string
    /**
     * Fraction of container width required to commit a swipe (0–1).
     * Default: 0.3
     */
    threshold?: number
}

/**
 * Renders all tab panels side-by-side in a flex track and animates between
 * them using `transform: translateX`.
 *
 * - Content follows the finger during a drag (no CSS transition).
 * - Snaps to the target tab with a 250ms ease-out on release.
 * - Snaps back to the current tab if the gesture didn't cross the threshold.
 * - Only the active panel and its immediate neighbours are mounted to keep
 *   memory and render cost low.
 */
export function SwipeableTabContent({
    tabs,
    activeId,
    onChange,
    children,
    className,
    threshold,
}: SwipeableTabContentProps) {
    const { flags } = useFeatureFlags()
    const { containerRef, trackRef } = useSwipeTabs({ tabs, activeId, onChange, threshold })

    const activeIndex = tabs.findIndex(t => t.id === activeId)

    if (!flags.swipeTabs) {
        return <>{children[activeIndex] ?? null}</>
    }

    return (
        <div ref={containerRef} className={className} style={{ overflow: 'hidden' }}>
            <div
                ref={trackRef}
                style={{
                    display: 'flex',
                    // Initial position — the hook takes over transform after mount.
                    transform: `translateX(-${Math.max(activeIndex, 0) * 100}%)`,
                    willChange: 'transform',
                }}
            >
                {children.map((child, i) => (
                    <div
                        key={i}
                        style={{
                            width: '100%',
                            flexShrink: 0,
                            minWidth: '100%',
                        }}
                    >
                        {/* Only mount the active panel and its immediate neighbours */}
                        {Math.abs(i - activeIndex) <= 1 ? child : null}
                    </div>
                ))}
            </div>
        </div>
    )
}
